import { createAsyncThunk } from '@reduxjs/toolkit';

import { handleError } from '../../../utility/helpers';
import { downloadFile, request, requestWithParam } from '../../../utility/apiClients';
import { REDUCER_ENUMS } from '../../../enums/applicationEnums';
import { APIS_ENDPOINTS, APIS_METHOD } from '../../../resources/api-constants';

export const getUsersList = createAsyncThunk(
    REDUCER_ENUMS.ADMIN_USERS_LIST,
    async (queryParams: any, { rejectWithValue }) => {
        try {
            const response = await requestWithParam(
                APIS_METHOD.GET,
                APIS_ENDPOINTS.ADMIN_USERS_LIST,
                {},
                queryParams,
                false
            );
            if (response) {
                const { results, count, total_count, current, lastPage, next, previous } = response.data;
                return { responseStatus: true, data: results, total: count, queryParams, usersCount: total_count, current, lastPage, next, previous };
            } else {
                return { responseStatus: false, data: [], total: 0, queryParams, studentCount: 0 };
            }
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getTiers = createAsyncThunk(
    REDUCER_ENUMS.TIERS,
    async (_, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.GET,
                APIS_ENDPOINTS.TIERS,
                {},
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const userProfileAction = createAsyncThunk(
    REDUCER_ENUMS.USER_PROFILE_ACTION,
    async (reqObj: any, { rejectWithValue }) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.USER_PROFILE_ACTION, reqObj, false);
            return response.data;
        } catch (err: any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const addTierAction = createAsyncThunk(
    REDUCER_ENUMS.ADD_TIER_ACTION,
    async (reqObj: any, { rejectWithValue }) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.ADD_TIER_ACTION, reqObj, false);
            return response.data;
        } catch (err: any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const updateTierAction = createAsyncThunk(
    REDUCER_ENUMS.UPDATE_TIER_ACTION,
    async ({ payload, id }: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.PUT,
                `${APIS_ENDPOINTS.UPDATE_TIER_ACTION}/${id}/`,
                payload,
                false
            );
            return response.data;
        } catch (err: any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getTiersList = createAsyncThunk(
    REDUCER_ENUMS.ADMIN_TIERS_LIST,
    async (queryParams: any, { rejectWithValue }) => {
        try {
            const response = await requestWithParam(
                APIS_METHOD.GET,
                APIS_ENDPOINTS.ADMIN_TIERS_LIST,
                {},
                queryParams,
                false
            );
            if (response) {
                const { results, count, total_count } = response.data;
                return { responseStatus: true, data: results, total: count, queryParams, tiersCount: total_count };
            } else {
                return { responseStatus: false, data: [], total: 0, queryParams, studentCount: 0 };
            }
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    }
);

export const deleteTier = createAsyncThunk(
    'delete-tier',
    async (tierId: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.DELETE,
                `/sbr2th_admin/tier/${tierId}`,
                {},
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getDashboardData = createAsyncThunk(
    REDUCER_ENUMS.ADMIN_DASHBOARD_ANALYTICS,
    async (_, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.GET,
                APIS_ENDPOINTS.ADMIN_DASHBOARD_ANALYTICS,
                {},
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    }
);

const clampAnalyticsDays = (days?: number) => Math.max(1, Math.min(365, Number(days) || 30));

export const getOverallAnalyticsDashboard = createAsyncThunk(
    'admin-analytics/overall-dashboard',
    async (
        { days = 30, includeTimeSeries = true }: { days?: number; includeTimeSeries?: boolean },
        { rejectWithValue }
    ) => {
        try {
            const safeDays = clampAnalyticsDays(days);
            const response = await requestWithParam(
                APIS_METHOD.GET,
                APIS_ENDPOINTS.ANALYTICS_DASHBOARD_OVERALL,
                {},
                {
                    days: safeDays,
                    include_time_series: includeTimeSeries ? 1 : 0,
                },
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    }
);

export const getAnalyticsDashboardUsers = createAsyncThunk(
    'admin-analytics/users-dashboard',
    async (
        {
            q = '',
            days = 30,
            page = 1,
            limit = 20,
        }: { q?: string; days?: number; page?: number; limit?: number },
        { rejectWithValue }
    ) => {
        try {
            const safeDays = clampAnalyticsDays(days);
            const response = await requestWithParam(
                APIS_METHOD.GET,
                APIS_ENDPOINTS.ANALYTICS_DASHBOARD_USERS,
                {},
                {
                    q,
                    days: safeDays,
                    page,
                    limit,
                },
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    }
);

export const getAnalyticsDashboardUserDetail = createAsyncThunk(
    'admin-analytics/user-dashboard-detail',
    async (
        { userId, days = 30 }: { userId: number; days?: number },
        { rejectWithValue }
    ) => {
        try {
            const safeDays = clampAnalyticsDays(days);
            const response = await requestWithParam(
                APIS_METHOD.GET,
                `${APIS_ENDPOINTS.ANALYTICS_DASHBOARD_USERS}${userId}/`,
                {},
                { days: safeDays },
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    }
);

export const getAnalyticsDashboardFlows = createAsyncThunk(
    'admin-analytics/flows-dashboard',
    async (
        {
            days = 30,
            pagePath,
            pageType,
        }: { days?: number; pagePath?: string; pageType?: string },
        { rejectWithValue }
    ) => {
        try {
            const safeDays = clampAnalyticsDays(days);
            const params: any = {
                days: safeDays,
            };
            if (pagePath) {
                params.page_path = pagePath;
            }
            if (pageType) {
                params.page_type = pageType;
            }

            const response = await requestWithParam(
                APIS_METHOD.GET,
                APIS_ENDPOINTS.ANALYTICS_DASHBOARD_FLOWS,
                {},
                params,
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    }
);

export const exportUsers = createAsyncThunk(
    'export-users-list',
    async (queryParam: any, { rejectWithValue }) => {
        try {
            // Convert queryParam object to a URL query string
            const queryString = new URLSearchParams(queryParam).toString();
            // Append query string to the API endpoint
            const url = `${APIS_ENDPOINTS.ADMIN_USERS_LIST}?${queryString}`;

            return await downloadFile(url);
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const userProfileSwitch = createAsyncThunk(
    'switch-user-profile',
    async (reqObj: any, { rejectWithValue }) => {
        try {
            const response = await request(APIS_METHOD.POST, '/sbr2th_admin/switch-user/', reqObj, false);
            return response.data;
        } catch (err: any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const changePlan = createAsyncThunk(
    'change-plan',
    async (reqObj: any, { rejectWithValue }) => {
        try {
            const response = await request(APIS_METHOD.POST, '/sbr2th_admin/change-plan/', reqObj, false);
            return response.data;
        } catch (err: any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const deleteUser = createAsyncThunk(
    REDUCER_ENUMS.DELETE_USER,
    async (deleteData: { userId: any, reason?: string }, { rejectWithValue }) => {
        try {
            const { userId, reason = "Complete user data removal" } = deleteData;
            const requestBody = {
                confirmation: true,
                reason: reason,
                delete_related_data: true
            };
            const response = await request(
                APIS_METHOD.DELETE,
                `${APIS_ENDPOINTS.DELETE_USER}/${userId}/`,
                requestBody,
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const markOTPVerified = createAsyncThunk(
    'mark-otp-verified',
    async (reqObj: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.POST,
                '/sbr2th_admin/mark-otp-verified/',
                reqObj,
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);